import { AxiosResponse } from 'axios'
import { Listing } from 'components/dropdown'
import { Routes } from 'config/routesEndpoints'
import { Counts, DynamicValues, Permissions, Tag } from 'config/types'
import {
  FieldItemValues,
  MediaItemKeys,
  ProcessedField,
} from 'config/types/field'
import { MediaItem } from 'config/types/imsModule'
import { BaseData, Order } from 'config/types/tables'
import dayjs from 'dayjs'
import { SetStateAction } from 'react'
import { ZodError, ZodType, z } from 'zod'

export interface DropdownProps {
  value?: string | number | null
  options?: Listing[] | undefined
}

export interface MultiDropDownProps {
  value: (string | number)[]
  options?: Listing[] | undefined
}

export const nonEmpty = (message: string) => (value: string) => {
  if (typeof value === 'string' && value.trim() === '') {
    return message
  }
  return undefined
}

export const checkItemStatus = (status: string) => {
  if (status === 'Pending') {
    return 'warning'
  } else if (status === 'Sold') {
    return 'error'
  } else if (status === 'In Auction') {
    return 'success'
  } else {
    return 'primary'
  }
}

export const checkMarketplaceTab = (locationPath: string) => {
  const pathSplit = locationPath.split('/')
  const location = pathSplit[pathSplit.length - 1]
  if (location === 'rent') {
    return 2
  } else if (location === 'auction') {
    return 3
  } else if (location === 'favourite') {
    return 4
  } else {
    return 1
  }
}

export const noSidebartoShow = (urlPath: string) => {
  const pathSplit = urlPath.split('/')
  const location = pathSplit[1]
  if (`/${location}` === Routes.rootUrl) {
    return true
  } else {
    return false
  }
}

export const containsNoImage = (inputString: string) => {
  return inputString?.includes('fs-no-image')
}

export const processAcceptText = (acceptText: string) => {
  if (acceptText.charAt(acceptText.length - 1) === 'e') {
    return acceptText.slice(0, -1)
  }
  return acceptText
}

export const currencyFormat = (
  currency: string | undefined,
  price: number | undefined
) => {
  const currencySymbols: { [key: string]: string } = {
    USD: '$',
    EUR: '€',
    CAD: 'C$',
    AUD: 'A$',
    GBP: '£',
    BRL: 'R$',
    // Add more currencies and symbols as needed
  }
  const currencyCode = currency?.match(/[A-Z]{3}/)?.[0] || 'USD'
  const currencySymbol = currencySymbols[currencyCode] || currencyCode
  const formattedPrice = price
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
      }).format(price)
    : ''
  // Check if formattedPrice is not an empty string before attempting to replace the currency symbol
  const numericPrice = formattedPrice
    ? formattedPrice.replace(currencySymbol, '').trim()
    : ''
  return `${currencySymbol} ${numericPrice}`
}

export const formatString = (value: string) => {
  if (value === '') {
    let route = Routes.home.split('/')[1]
    route = route.charAt(0).toUpperCase() + route.slice(1)
    return route
  }
  if (value.startsWith('/')) {
    value = value.slice(1)
  }
  if (/[A-Z]/.test(value)) {
    let words = value.split(/(?=[A-Z])/)
    return words
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  } else {
    return value.charAt(0).toUpperCase() + value.slice(1)
  }
}

export const getValidationErrors = (
  error: z.ZodError
): Record<string, string> => {
  const errors: Record<string, string> = {}

  for (const errorItem of error.errors) {
    if (errorItem.path) {
      errors[errorItem.path.join('.')] = errorItem.message
    }
  }

  return errors
}

export const arraySort = (arr: any[]) => {
  return arr.slice().sort((a, b) => {
    const titleA = a.title.toLowerCase()
    const titleB = b.title.toLowerCase()
    if (titleA < titleB) {
      return -1
    }
    if (titleA > titleB) {
      return 1
    }
    return 0
  })
}

export const descendingComparator = <T>(a: T, b: T, orderBy: keyof T) => {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

export const getComparator = <Key extends keyof any>(
  order: Order,
  orderBy: Key
): ((
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

export const handleApiRequest =
  <T>(
    onSuccess: (data: AxiosResponse<T>) => void,
    onError: (error: SetStateAction<string>) => void
  ) =>
  (data: AxiosResponse<T> | Error) => {
    if (data instanceof Error) {
      onError(data.message)
    } else {
      onSuccess(data)
    }
  }

type Resolver<T> = (
  data: T
) => Promise<{ values: T; errors: Record<string, string> }>

export const createResolver =
  <T>(schema: ZodType<T, any, any>): Resolver<T> =>
  async data => {
    try {
      const validatedData = schema.parse(data)
      return {
        values: validatedData,
        errors: {},
      }
    } catch (error) {
      const validationErrors = getValidationErrors(error as ZodError)
      return {
        values: {} as T,
        errors: validationErrors,
      }
    }
  }

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
export const stableSort = <T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) => {
  const stabilizedThis = array?.map((el, index) => [el, index] as [T, number])
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) {
      return order
    }
    return a[1] - b[1]
  })
  return stabilizedThis?.map(el => el[0])
}

export const getDropdownValue = ({ value, options }: DropdownProps) => {
  let selectedValue: Listing | undefined
  selectedValue = options?.find((item: { value: any }) => {
    return item?.value === value
  })
  return selectedValue || undefined
}

export const getTrueKeysArray = (
  obj: DynamicValues | string[]
): string[] | DynamicValues => {
  if (Array.isArray(obj)) {
    let fieldValue: DynamicValues = {}
    obj.forEach(item => {
      fieldValue[item] = true
    })
    return fieldValue
  }
  return (
    obj &&
    Object.keys(obj)
      .filter(key => obj[key] === true)
      .map(key => (key.startsWith('id_') ? key.slice(3) : key))
  )
}

export const updateMultiSelectDropdownValue = ({
  value,
  options,
}: MultiDropDownProps) => {
  const multiSelect = options
    ?.filter(item => {
      return value?.includes(item?.value) && item
    })
    .sort((a, b) => {
      return value.indexOf(a.value) - value.indexOf(b.value)
    })

  return multiSelect
}

export const getDynamicFieldValues = (value: any[]) => {
  const fieldValue = value?.map(item => item?.id)
  return fieldValue
}

export function updateTableDataItem<T extends BaseData>(
  arr: T[],
  index: number | undefined,
  newItem: T,
  keyToUpdate: keyof T & keyof Counts
): T[] {
  if (typeof index !== 'number' || index < 0 || index >= arr.length) {
    return arr
  }
  const targetArray = arr[index][keyToUpdate]
  if (Array.isArray(targetArray) && newItem) {
    targetArray.unshift(newItem)
    switch (keyToUpdate) {
      case 'subCategories':
        arr[index]._count.subCategories += 1
        break
      case 'sections':
        arr[index]._count.sections += 1
        break
      case 'fields':
        arr[index]._count.fields += 1
        break
      case 'types':
        arr[index]._count.types += 1
        break
      default:
        break
    }
  } else {
    if (arr[index][keyToUpdate] === undefined) {
      arr[index][keyToUpdate as keyof T] = [newItem] as unknown as T[keyof T]
    }
  }
  return arr
}

export function updateTableData<T>(
  arr: T[],
  index?: number,
  newItem?: T,
  add?: boolean
): T[] {
  if (add && newItem !== undefined) {
    // If add is true and newItem exists, insert newItem at the beginning
    let newArr = [newItem, ...arr]
    // Check if the new array exceeds 10 items, and if so, remove the last item
    // if (newArr.length > 10) {
    //   newArr = newArr.slice(0, 10)
    // }
    return newArr
  } else if (newItem !== undefined && index !== undefined) {
    // If newItem exists and index is provided, insert it at the specified index
    return [...arr.slice(0, index), newItem, ...arr.slice(index + 1)]
  } else if (index !== undefined) {
    // If newItem is not provided, remove the element at the specified index
    return [...arr.slice(0, index), ...arr.slice(index + 1)]
  } else {
    // If neither newItem nor index is provided, return the original array
    return arr
  }
}

export const getImageName = (
  input: string | { url: string }
): string | undefined => {
  let url: string | undefined
  if (typeof input === 'string') {
    url = input
  } else if (
    typeof input === 'object' &&
    input !== null &&
    'url' in input &&
    typeof input.url === 'string'
  ) {
    url = input.url
  }
  if (!url) return undefined
  const parts = url.split('--')
  return parts.pop()
}

export const imageUrlToBlob = (url: string) => {
  return fetch(url)
    .then(response => response.blob())
    .then(
      blob =>
        new Promise((resolve, reject) => {
          const fileName = getImageName(url)
          const file = new File([blob], fileName || '', { type: blob.type })
          resolve(file)
        })
    )
}

export const getTagsValue = (data?: Tag[]) => {
  const fieldValue: Array<number | undefined> =
    data?.map(item => {
      return item?.id
    }) ?? []

  return fieldValue
}

export const MaxWordsValidator = (maxWords: number) => (value: string) => {
  const wordCount = value.trim().split(/\s+/).length
  if (wordCount > maxWords) {
    throw new z.ZodError([])
  }
  return value
}
export function getUniqueArray<T>(arr: T[], keyName: keyof T): T[] {
  const uniqueArray = [...new Map(arr.map(x => [x[keyName], x])).values()]
  return uniqueArray
}

// export const getContrastRatio = (color1?: string, color2?: string): number => {
//   const luminance1 = chroma(color1 || 'aquamarine').luminance()
//   const luminance2 = chroma(color2 || 'hotpink').luminance()
//   const brightest = Math.max(luminance1, luminance2)
//   const darkest = Math.min(luminance1, luminance2)
//   // return (brightest + 0.05) / (darkest + 0.05)
//   return luminance1
// }

export const getLuminance = (hex?: string): number => {
  const rgb = parseInt((hex || '').slice(1), 16)
  const r = (rgb >> 16) & 0xff
  const g = (rgb >> 8) & 0xff
  const b = (rgb >> 0) & 0xff

  const sRGB = [r, g, b].map(value => {
    value /= 255
    return value <= 0.03928
      ? value / 12.92
      : Math.pow((value + 0.055) / 1.055, 2.4)
  })

  return 0.2126 * sRGB[0] + 0.7152 * sRGB[1] + 0.0722 * sRGB[2]
}
export const getContrastRatio = (
  foreground?: string,
  background?: string
): number => {
  const lum1 = getLuminance(foreground)
  const lum2 = getLuminance(background)
  const brightest = Math.max(lum1, lum2)
  const darkest = Math.min(lum1, lum2)
  return (brightest + 0.05) / (darkest + 0.05)
}
export const hexToHSL = (hex: string) => {
  if (!hex) return null
  let r = parseInt(hex.slice(1, 3), 16)
  let g = parseInt(hex.slice(3, 5), 16)
  let b = parseInt(hex.slice(5, 7), 16)
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0,
    s,
    l = (max + min) / 2
  if (max === min) {
    s = 0
  } else {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }
  return { h: h * 360, s: s * 100, l: l * 100 }
}

export const createHSLVariants = (hex: string) => {
  const hsl = hexToHSL(hex)
  if (!hsl) return []
  const { h, s, l } = hsl
  const variants = []
  const lightVariants = 7
  const darkVariants = 4

  for (let i = 1; i <= lightVariants; i++) {
    let lightness = l + (i * (100 - l)) / (lightVariants + 1)
    variants.push(
      `hsl(${h.toFixed(2)}, ${s.toFixed(2)}%, ${lightness.toFixed(2)}%)`
    )
  }

  variants.push(`hsl(${h.toFixed(2)}, ${s.toFixed(2)}%, ${l.toFixed(2)}%)`)

  for (let i = 1; i <= darkVariants; i++) {
    let lightness = l - (i * l) / (darkVariants + 1)
    variants.unshift(
      `hsl(${h.toFixed(2)}, ${s.toFixed(2)}%, ${lightness.toFixed(2)}%)`
    )
  }
  return variants
}

export function getOrderData<T>(key: keyof T, ids?: number[], arr?: T[]): T[] {
  const updatedData = (arr || [])?.sort(
    (a, b) =>
      (ids || []).indexOf(a[key] as unknown as number) -
      (ids || []).indexOf(b[key] as unknown as number)
  )
  return getUniqueArray(updatedData, 'id' as keyof T)
}

export const containsHtmlTags = (inputString: string) => {
  const regex = /<[^>]*>/g
  return regex.test(inputString)
}

export const stripHtmlTags = (inputString: string) => {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = inputString
  return tempDiv.textContent || tempDiv.innerText || ''
}

export const getValidOrderBy = (
  key: string | undefined,
  sortableKeys: string[],
  defaultKey: string = 'id'
): string => {
  return sortableKeys.includes(key || '') ? key || defaultKey : defaultKey
}

export const formatValues = (
  values: { value: string; text: string }[],
  key: 'value' | 'text',
  type?: string
) => {
  if (['DAY_CALENDAR', 'CALENDAR'].includes(type || '')) {
    const formattedValues = values.map(v => v[key]).filter(v => v.trim())
    if (formattedValues.length === 0) {
      return 'N/A'
    }
    return formattedValues.map(v => dayjs(v).format('MMMM DD, YYYY')).join(', ')
  }
  const formattedValue = values
    .map(v => v[key])
    .filter(v => v.trim())
    .join(', ')
  return formattedValue || (key === 'text' ? '' : 'N/A')
}

// const CATEGORY_MAP = {
//   IMAGE: ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg', '.webp'],
//   VIDEO: ['.mp4', '.mov', '.avi', '.mkv', '.webm'],
//   DOCUMENT: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt'],
// }

// const isFieldObject = (value: FieldItemValues) => {
//   return (
//     typeof value === 'object' &&
//     value !== null &&
//     'value' in value &&
//     'text' in value
//   )
// }

// const isUrlArray = (value: string[]) => {
//   return (
//     Array.isArray(value) &&
//     value.every(item => typeof item === 'string' && item.startsWith('http'))
//   )
// }

// const categorizeFile = (url: string) => {
//   const m = url.match(/\.[^/.]+$/)
//   const ext = m?.[0].toLowerCase() ?? ''
//   for (const cat of Object.keys(CATEGORY_MAP) as Array<
//     keyof typeof CATEGORY_MAP
//   >) {
//     if (CATEGORY_MAP[cat].includes(ext)) return cat
//   }
//   return 'UNKNOWN'
// }

export const processData = (
  data: Record<string, unknown>,
  excludeFields: string[] = []
): ProcessedField[] => {
  return Object.entries(data)
    .filter(([k]) => !excludeFields.includes(k))
    .map(([id, raw]) => {
      const media: MediaItemKeys[] = []
      let mediaCounter = 0
      const pushMediaFromArray = (arr: unknown[]) => {
        arr.forEach(m => {
          const rec =
            m && typeof m === 'object'
              ? (m as Record<string, unknown>)
              : { key: String(m) }
          media.push({
            key: String(
              (rec as any).key ||
                (rec as any).id ||
                (rec as any).url ||
                `${id}-${mediaCounter}`
            ),
            type: String(
              (rec as any).type ||
                ((rec as any).mime &&
                String((rec as any).mime).includes('image')
                  ? 'IMAGE'
                  : 'UNKNOWN')
            ),
          })
          mediaCounter++
        })
      }
      let values: FieldItemValues[] = []
      if (Array.isArray(raw)) {
        values = raw.map(s => ({ value: s as any, text: '' }))
      } else if (raw && typeof raw === 'object') {
        const obj = raw as Record<string, unknown>
        if (Array.isArray(obj.media)) {
          pushMediaFromArray(obj.media as unknown[])
        }
        if (Array.isArray(obj.value)) {
          values = (obj.value as unknown[]).map(v => ({
            value: v as any,
            text:
              obj.text !== undefined &&
              obj.text !== '' &&
              typeof obj.text === 'string'
                ? (obj.text as FieldItemValues['text']) ?? ''
                : '',
          }))
        } else if (obj.value !== undefined || obj.text !== undefined) {
          values = [
            {
              value: (obj.value as string | number | boolean) ?? '',
              text: (obj.text as FieldItemValues['text']) ?? '',
            },
          ]
        } else {
          values = [{ value: '', text: '' }]
        }
      } else {
        values = [{ value: (raw as any) ?? '', text: '' }]
      }
      return media.length > 0 ? { id, values, media } : { id, values }
    })
}

export const urlToFile = async (
  url: string,
  fileName: string
): Promise<File> => {
  const response = await fetch(url)
  const blob = await response.blob()
  return new File([blob], fileName, { type: blob.type })
}

export const isItemArray = (value: unknown): value is MediaItem[] =>
  Array.isArray(value) &&
  value.every(item => typeof item === 'object' && 'type' in item)

export const getLastTask = (tasks: any[]): any | null => {
  return tasks && tasks.length > 0 ? tasks[tasks.length - 1] : null
}

export const userPermission = (
  user: Permissions[],
  key: string,
  user_type: string
): boolean => {
  if (user_type === 'admin') return true
  return user?.some((perm: Permissions) => perm.key === key) ?? false
}
